/**
 * UpgradeEmailModal Component
 * Captures emails before redirecting to pricing for remarketing
 */

import React, { useState } from 'react';
import { X, Sparkles, ArrowRight, Gift, Bell, Zap } from 'lucide-react';

const UpgradeEmailModal = ({ isOpen, onClose, onSubmit, featureName }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Store the lead
      await onSubmit(email, featureName);
      // Redirect to pricing
      window.location.href = '/#pricing';
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
    window.location.href = '/#pricing';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="email-capture-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-gray-900 rounded-2xl border border-gray-700 shadow-2xl overflow-hidden animate-fadeIn">
        {/* Gradient top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500" />
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors"
          data-testid="close-email-modal"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-8">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-xl animate-pulse" />
              <div className="relative w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                <Gift className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-7 h-7 bg-yellow-500 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-yellow-900" />
              </div>
            </div>
          </div>

          {/* Headline */}
          <h2 className="text-2xl font-bold text-white text-center mb-2">
            Wait! Get 20% Off Pro 🎉
          </h2>
          <p className="text-gray-400 text-center mb-6">
            Drop your email and we'll send you an exclusive discount code + notify you of flash sales.
          </p>

          {/* Benefits */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Zap className="w-4 h-4 text-purple-400" />
              </div>
              <span className="text-gray-300">Exclusive 20% founder discount</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center">
                <Bell className="w-4 h-4 text-pink-400" />
              </div>
              <span className="text-gray-300">Early access to new features</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Gift className="w-4 h-4 text-orange-400" />
              </div>
              <span className="text-gray-300">Free marketing tips & templates</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email..."
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                data-testid="email-capture-input"
              />
              {error && (
                <p className="mt-2 text-sm text-red-400">{error}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="claim-discount-btn"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Claim My Discount
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* Skip link */}
          <button
            onClick={handleSkip}
            className="w-full mt-4 py-2 text-gray-500 hover:text-gray-400 text-sm transition-colors"
            data-testid="skip-email-capture"
          >
            No thanks, take me to pricing →
          </button>

          {/* Privacy note */}
          <p className="mt-4 text-xs text-gray-600 text-center">
            We respect your privacy. Unsubscribe anytime.
          </p>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
      `}</style>
    </div>
  );
};

export default UpgradeEmailModal;
